import { useMemo, useState } from 'react';

import produce from 'immer';
import { maxBy } from 'lodash';

import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { maxValueScale } from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.utils';

enum ToggleBy {
    FluxIndex = 'Flux index',
    CriticalityScore = 'Criticality Score',
}

const serializeData = (object: any) => {
    if (!object) return undefined;
    return produce(object, (draft: any) => {
        if (draft.cardProperties?.metrics) {
            draft.cardProperties.metrics.forEach((metric: any) => {
                if (metric.link) {
                    metric.link = metric.link.replace(
                        '/dashboard/project/version-compare',
                        '/dashboard/planning/version-compare',
                    );
                }
            });
        }
    });
};

export const useChangeSummaryLogic = ({ api, setIsPayloadReady, widgetViewMode }) => {
    const projectLevelData = api.getSummaryData.instance.data?.summaryCards
        ? api.getSummaryData.instance.data.summaryCards.find((item) => item?.cardTitle?.cardType === 'ProjectLevel')
        : [];

    const activityLevelData = useMemo(() => {
        const data = api.getSummaryData.instance.data;
        const activityLevel = data?.summaryCards?.find((item) => item?.cardTitle?.cardType === 'ActivityLevel');
        if (activityLevel) {
            return serializeData(activityLevel);
        } else {
            return [];
        }
    }, [api.getSummaryData.instance.data]);

    const graphData = api.getSummaryData.instance.data?.summaryCards
        ? api.getSummaryData.instance.data.summaryCards.find((item) => item?.cardTitle?.cardType === 'FluxIndex')
        : [];

    const isSummaryData =
        Boolean(projectLevelData?.cardProperties) &&
        Boolean(
            activityLevelData &&
                typeof activityLevelData === 'object' &&
                !Array.isArray(activityLevelData) &&
                (activityLevelData as any)?.cardProperties,
        );

    const [toggle, setToggle] = useState<ToggleBy>(ToggleBy.CriticalityScore);
    const selectToggle = (event) => {
        setToggle(event.target.value);
    };
    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });
    const cardTrendsIndex = Object.values(ToggleBy).findIndex((item) => item === toggle);
    const maxValuePoint = graphData?.cardTrends && maxBy(graphData.cardTrends[cardTrendsIndex].graphPoints, 'score');
    const maxValue = maxValuePoint ? maxValuePoint?.score * maxValueScale : undefined;

    function isPayloadReady() {
        const currentPayload = api.getSummaryData.payload;
        if (!currentPayload) return;
        if (
            currentPayload.latestVersionId === undefined ||
            currentPayload.compareVersionId === undefined ||
            currentPayload.contractId === null ||
            currentPayload.contractId === undefined ||
            currentPayload.projectId === null ||
            currentPayload.projectId === undefined
        ) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    }

    return {
        logic: {
            isSummaryData,
            selectToggle,
            maxValue,
            projectLevelData,
            activityLevelData,
            graphData,
            toggle,
            setToggle,
            cardTrendsIndex,
        },
    };
};
